/**
 * Minimal GMX V2 read ABIs for the position / ticket reader.
 *
 * Struct layouts (Deposit.Props, Withdrawal.Props, GlvDeposit.Props,
 * GlvWithdrawal.Props) are extracted verbatim from the deployed Reader /
 * GlvReader ABIs — field order matters for viem to decode the named fields.
 */

const depositProps = {
  type: 'tuple',
  components: [
    {
      name: 'addresses',
      type: 'tuple',
      components: [
        { name: 'account', type: 'address' },
        { name: 'receiver', type: 'address' },
        { name: 'callbackContract', type: 'address' },
        { name: 'uiFeeReceiver', type: 'address' },
        { name: 'market', type: 'address' },
        { name: 'initialLongToken', type: 'address' },
        { name: 'initialShortToken', type: 'address' },
        { name: 'longTokenSwapPath', type: 'address[]' },
        { name: 'shortTokenSwapPath', type: 'address[]' },
      ],
    },
    {
      name: 'numbers',
      type: 'tuple',
      components: [
        { name: 'initialLongTokenAmount', type: 'uint256' },
        { name: 'initialShortTokenAmount', type: 'uint256' },
        { name: 'minMarketTokens', type: 'uint256' },
        { name: 'updatedAtTime', type: 'uint256' },
        { name: 'executionFee', type: 'uint256' },
        { name: 'callbackGasLimit', type: 'uint256' },
        { name: 'srcChainId', type: 'uint256' },
      ],
    },
    {
      name: 'flags',
      type: 'tuple',
      components: [{ name: 'shouldUnwrapNativeToken', type: 'bool' }],
    },
    { name: '_dataList', type: 'bytes32[]' },
  ],
} as const

const withdrawalProps = {
  type: 'tuple',
  components: [
    {
      name: 'addresses',
      type: 'tuple',
      components: [
        { name: 'account', type: 'address' },
        { name: 'receiver', type: 'address' },
        { name: 'callbackContract', type: 'address' },
        { name: 'uiFeeReceiver', type: 'address' },
        { name: 'market', type: 'address' },
        { name: 'longTokenSwapPath', type: 'address[]' },
        { name: 'shortTokenSwapPath', type: 'address[]' },
      ],
    },
    {
      name: 'numbers',
      type: 'tuple',
      components: [
        { name: 'marketTokenAmount', type: 'uint256' },
        { name: 'minLongTokenAmount', type: 'uint256' },
        { name: 'minShortTokenAmount', type: 'uint256' },
        { name: 'updatedAtTime', type: 'uint256' },
        { name: 'executionFee', type: 'uint256' },
        { name: 'callbackGasLimit', type: 'uint256' },
        { name: 'srcChainId', type: 'uint256' },
      ],
    },
    {
      name: 'flags',
      type: 'tuple',
      components: [{ name: 'shouldUnwrapNativeToken', type: 'bool' }],
    },
    { name: '_dataList', type: 'bytes32[]' },
  ],
} as const

export const GmxReaderAbi = [
  {
    name: 'getDeposit',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'key', type: 'bytes32' },
    ],
    outputs: [depositProps],
  },
  {
    name: 'getWithdrawal',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'key', type: 'bytes32' },
    ],
    outputs: [withdrawalProps],
  },
] as const

const glvDepositProps = {
  name: '',
  type: 'tuple[]',
  components: [
    {
      name: 'addresses',
      type: 'tuple',
      components: [
        { name: 'glv', type: 'address' },
        { name: 'account', type: 'address' },
        { name: 'receiver', type: 'address' },
        { name: 'callbackContract', type: 'address' },
        { name: 'uiFeeReceiver', type: 'address' },
        { name: 'market', type: 'address' },
        { name: 'initialLongToken', type: 'address' },
        { name: 'initialShortToken', type: 'address' },
        { name: 'longTokenSwapPath', type: 'address[]' },
        { name: 'shortTokenSwapPath', type: 'address[]' },
      ],
    },
    {
      name: 'numbers',
      type: 'tuple',
      components: [
        { name: 'marketTokenAmount', type: 'uint256' },
        { name: 'initialLongTokenAmount', type: 'uint256' },
        { name: 'initialShortTokenAmount', type: 'uint256' },
        { name: 'minGlvTokens', type: 'uint256' },
        { name: 'updatedAtTime', type: 'uint256' },
        { name: 'executionFee', type: 'uint256' },
        { name: 'callbackGasLimit', type: 'uint256' },
        { name: 'srcChainId', type: 'uint256' },
      ],
    },
    {
      name: 'flags',
      type: 'tuple',
      components: [
        { name: 'shouldUnwrapNativeToken', type: 'bool' },
        { name: 'isMarketTokenDeposit', type: 'bool' },
      ],
    },
    { name: '_dataList', type: 'bytes32[]' },
  ],
} as const

const glvWithdrawalProps = {
  name: '',
  type: 'tuple[]',
  components: [
    {
      name: 'addresses',
      type: 'tuple',
      components: [
        { name: 'glv', type: 'address' },
        { name: 'market', type: 'address' },
        { name: 'account', type: 'address' },
        { name: 'receiver', type: 'address' },
        { name: 'callbackContract', type: 'address' },
        { name: 'uiFeeReceiver', type: 'address' },
        { name: 'longTokenSwapPath', type: 'address[]' },
        { name: 'shortTokenSwapPath', type: 'address[]' },
      ],
    },
    {
      name: 'numbers',
      type: 'tuple',
      components: [
        { name: 'glvTokenAmount', type: 'uint256' },
        { name: 'minLongTokenAmount', type: 'uint256' },
        { name: 'minShortTokenAmount', type: 'uint256' },
        { name: 'updatedAtTime', type: 'uint256' },
        { name: 'executionFee', type: 'uint256' },
        { name: 'callbackGasLimit', type: 'uint256' },
        { name: 'srcChainId', type: 'uint256' },
      ],
    },
    {
      name: 'flags',
      type: 'tuple',
      components: [{ name: 'shouldUnwrapNativeToken', type: 'bool' }],
    },
    { name: '_dataList', type: 'bytes32[]' },
  ],
} as const

export const GmxGlvReaderAbi = [
  {
    name: 'getAccountGlvDeposits',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'account', type: 'address' },
      { name: 'start', type: 'uint256' },
      { name: 'end', type: 'uint256' },
    ],
    outputs: [glvDepositProps],
  },
  {
    name: 'getAccountGlvWithdrawals',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'account', type: 'address' },
      { name: 'start', type: 'uint256' },
      { name: 'end', type: 'uint256' },
    ],
    outputs: [glvWithdrawalProps],
  },
] as const

export const GmxDataStoreAbi = [
  {
    name: 'getBytes32ValuesAt',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'setKey', type: 'bytes32' },
      { name: 'start', type: 'uint256' },
      { name: 'end', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bytes32[]' }],
  },
] as const

const priceProps = {
  type: 'tuple',
  components: [
    { name: 'min', type: 'uint256' },
    { name: 'max', type: 'uint256' },
  ],
} as const

/** SyntheticsReader + GlvReader pricing views used to value GM/GLV in USD. */
export const GmxPricingAbi = [
  {
    name: 'getMarketTokenPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      {
        name: 'market',
        type: 'tuple',
        components: [
          { name: 'marketToken', type: 'address' },
          { name: 'indexToken', type: 'address' },
          { name: 'longToken', type: 'address' },
          { name: 'shortToken', type: 'address' },
        ],
      },
      { ...priceProps, name: 'indexTokenPrice' },
      { ...priceProps, name: 'longTokenPrice' },
      { ...priceProps, name: 'shortTokenPrice' },
      { name: 'pnlFactorType', type: 'bytes32' },
      { name: 'maximize', type: 'bool' },
    ],
    outputs: [
      { name: 'marketTokenPrice', type: 'int256' },
      {
        name: 'poolValueInfo',
        type: 'tuple',
        components: [
          { name: 'poolValue', type: 'int256' },
          { name: 'longPnl', type: 'int256' },
          { name: 'shortPnl', type: 'int256' },
          { name: 'netPnl', type: 'int256' },
          { name: 'longTokenAmount', type: 'uint256' },
          { name: 'shortTokenAmount', type: 'uint256' },
          { name: 'longTokenUsd', type: 'uint256' },
          { name: 'shortTokenUsd', type: 'uint256' },
          { name: 'totalBorrowingFees', type: 'uint256' },
          { name: 'borrowingFeePoolFactor', type: 'uint256' },
          { name: 'impactPoolAmount', type: 'uint256' },
          { name: 'lentImpactPoolAmount', type: 'uint256' },
        ],
      },
    ],
  },
] as const

export const GmxGlvPricingAbi = [
  {
    name: 'getGlvInfo',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'glv', type: 'address' },
    ],
    outputs: [
      {
        name: '',
        type: 'tuple',
        components: [
          {
            name: 'glv',
            type: 'tuple',
            components: [
              { name: 'glvToken', type: 'address' },
              { name: 'longToken', type: 'address' },
              { name: 'shortToken', type: 'address' },
            ],
          },
          { name: 'markets', type: 'address[]' },
        ],
      },
    ],
  },
  {
    name: 'getGlvTokenPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'dataStore', type: 'address' },
      { name: 'marketAddresses', type: 'address[]' },
      { ...priceProps, name: 'indexTokenPrices', type: 'tuple[]' },
      { ...priceProps, name: 'longTokenPrice' },
      { ...priceProps, name: 'shortTokenPrice' },
      { name: 'glv', type: 'address' },
      { name: 'maximize', type: 'bool' },
    ],
    outputs: [
      { name: 'glvTokenPrice', type: 'uint256' },
      { name: 'glvValue', type: 'uint256' },
      { name: 'glvSupply', type: 'uint256' },
    ],
  },
] as const

/** DataStore `getUint` — for the execution-fee gas-limit / multiplier keys. */
export const GmxDataStoreUintAbi = [
  {
    name: 'getUint',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'key', type: 'bytes32' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

export const GmxErc20BalanceAbi = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

/** GLV token's internally-tracked GM balance per market (the value GMX's
 *  per-market GLV cap is enforced against — not the ERC-20 `balanceOf`). */
export const GmxGlvTokenBalancesAbi = [
  {
    name: 'tokenBalances',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'token', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const
